// SPDX-FileCopyrightText: Copyright 2026 OP Pohjola (https://op.fi). All Rights Reserved.
// SPDX-License-Identifier: LicenseRef-OpPohjolaAllRightsReserved

import axios from 'axios';

class UnexpectedContentTypeError extends Error {
    constructor(contentType, response) {
        super(`Unexpected content-type: ${contentType || 'missing'}`);
        this.name = 'UnexpectedContentTypeError';
        this.code = 'ERR_UNEXPECTED_CONTENT_TYPE';
        this.contentType = contentType;
        this.response = response;
    }
}

class FullpageloginApi {
    constructor() {
        this.api = axios.create({
            headers: { Accept: 'application/json' },
            timeout: 10000,
        });
        this.api.interceptors.response.use((response) => {
            const contentLengthHeader = response.headers?.['content-length'];
            const contentLength = !contentLengthHeader ? 0 : Number(contentLengthHeader);
            const contentType = response.headers?.['content-type'] ?? '';
            const isEmptyResponse = contentLength === 0;
            const isJson = contentType.startsWith('application/json');

            if (!isEmptyResponse && !isJson) {
                throw new UnexpectedContentTypeError(contentType, response);
            }

            return response;
        });
    }
    async apiRequest(httpMethod, requestPath, requestBody) {
        try {
            const response = await this.api.request({
                url: requestPath,
                method: httpMethod,
                data: requestBody,
            });
            return response.data;
        } catch (error) {
            console.debug('fullpagelogin API call failed', error.response);
            if (error instanceof UnexpectedContentTypeError) throw error;
            const apiError = new Error();
            apiError.response = error.response;
            throw apiError;
        }
    }

    async logout() {
        return await this.apiRequest('POST', '/authentication/web/v3/rest/logout');
    }

    async selectRepresentation(representation, serviceFilter) {
        let qs = `?REPDATA=${encodeURIComponent(representation.encryptedSelectString)}`;
        if (serviceFilter) qs += `&SERVICE=${encodeURIComponent(serviceFilter)}`;
        return await this.apiRequest('POST', `/authentication/representation/v3/rest/select-representation${qs}`);
    }

    async selectRepresentationByServiceFilter(serviceFilter) {
        return await this.apiRequest(
            'POST',
            `/authentication/representation/v3/rest/select-representation?SERVICE=${encodeURIComponent(serviceFilter)}`
        );
    }

    async selectRepresentationByTypes(repTypes) {
        const qs = repTypes.length > 0 ? '?REPTYPE=' + encodeURIComponent(repTypes.join()) : '';
        return await this.apiRequest('POST', `/authentication/representation/v3/rest/select-representation${qs}`);
    }

    async getRepresentationListByServiceFilter(serviceFilter) {
        return await this.apiRequest('GET', `/authentication/representation/v3/rest/representations?SERVICE=${encodeURIComponent(serviceFilter)}`);
    }

    async getRepresentationListByTypes(repTypes) {
        const qs = repTypes.length > 0 ? '?REPTYPE=' + encodeURIComponent(repTypes.join()) : '';
        return await this.apiRequest('GET', `/authentication/representation/v3/rest/representations${qs}`);
    }
}

const api = new FullpageloginApi();
export { UnexpectedContentTypeError };
export default api;
